import { Brick, registerBrick, Query, Predicate, User, Order, Process, QueryResult } from 'olympe';
import { getLogger } from '@olympeio/core';
import { switchMap } from 'rxjs';
import AndritzUser from '../../../models/AndritzUser';
import Assessment from '../../../models/Assessment';

export default class SubscribeToUserAssessments extends Brick {

    /**
     * @override
     * So that the brick also gets triggered when it switches between online/offline
     */
    setupExecution($) {
        return Process.isOnline($).pipe(switchMap(() => {
            return super.setupExecution($);
        }));
    }

    /**
     * @override
     * @protected
     * @param {!BrickContext} $
     * @param {User} currentUser
     * @param {function(QueryResult)} setAssessmentList
     */
    async update($, [currentUser], [setAssessmentList]) {
        const logger = getLogger('SubscribeToUserAssessments');

        const andritzUserQuery = (
            await Query.instancesOf(AndritzUser)
                .andReturn()
                .filter(Predicate.equals(AndritzUser.usernameProp, currentUser.get(User.loginProp)))
                .follow(AndritzUser.roleRel)
                .andReturn()
                .execute()
        ).getFirst();

        if (!andritzUserQuery) {
            logger.warn(`Failed to retrieve the user to subscribe to its assessments`);
            setAssessmentList(QueryResult.empty());
            return;
        }

        const [andritzUser, role] = andritzUserQuery;

        // Load assessment + assignee so that internal subscriptions to get the assignee on each renderer of the datagrid is actually
        // executed on the cache. And only one subscription is sent to the database.
        let preloadQuery = Query.instancesOf(Assessment)
            .andReturn()
            .filter(Predicate.not(Predicate.equals(Assessment.deletedProp, true)))
            .follow(Assessment.assigneeRel).andReturn()
            .back();

        let assessmentQuery = Query.instancesOf(Assessment)
            .andReturn()
            .filter(Predicate.not(Predicate.equals(Assessment.deletedProp, true)))
            .sortBy(Assessment.lastModifiedProp, Order.DESC);

        if (role.getRoleValue() === 'GUEST') {
            preloadQuery = preloadQuery
                .follow(Assessment.assigneeRel)
                .filter(Predicate.in(andritzUser));
            assessmentQuery = assessmentQuery
                .follow(Assessment.assigneeRel)
                .filter(Predicate.in(andritzUser));
        } else {
            preloadQuery = preloadQuery
                .follow(Assessment.creatorRel)
                .filter(Predicate.in(andritzUser));
            assessmentQuery = assessmentQuery
                .follow(Assessment.creatorRel)
                .filter(Predicate.in(andritzUser));
        }

        // Subscribing to the preload query is sufficient for the data cloud to know that the assessmentQuery is "covered"
        // and can stay local and be executed on the cache.
        preloadQuery.observe($).subscribe((_preloadResult) => { /* empty */ });

        assessmentQuery.observe($).subscribe((result) => {
            setAssessmentList(result);
        });
    }
}

registerBrick('018a836c9138fe810c6d', SubscribeToUserAssessments);
